import DocumentLink from '../v2/documents/DocumentLink'
/**
 * Phase 2.5: parked-document queue.
 *
 * Officers see every doc the pipeline could not link to a fund — with the
 * reason, the resolved-entity count, and timestamps. The system retries
 * orphans automatically when new entities arrive; this page is the
 * "where did my upload go?" surface.
 *
 * An orphan resolves by completing the object chain — the officer adds the
 * missing intermediate entity/instrument/position that links the doc up to a
 * fund-entity chain head, which auto-triggers the orphan-retry re-walk.
 *
 * G2 (#812): the re-walk also fires automatically when new entities arrive,
 * so a stuck orphan may just need a nudge. The per-row "Retry" button
 * re-runs fund derivation for that one document on demand (the same reclaim
 * the listener performs) — low-risk: it either resolves and continues the
 * pipeline, or 409s with "still not derivable" and nothing changes.
 * Force-attach (pinning a fund by hand) is a heavier, attribution-pinning
 * action — the backend supports it (action='force_attach') but we keep it
 * off this queue for now (see TODO below); Ale to confirm orphans are meant
 * to stay auto-retry-first.
 *
 * Backend: GET /api/orphans + POST /api/orphans/{id}/action
 * (src/nolos/orphans/router.py).
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch, ApiError } from '../lib/api'
import { RefreshCw, AlertTriangle, FileText } from 'lucide-react'
import { formatOfficerDate } from '../lib/officerFormat'

type OrphanReason =
  'no_entities_resolved' | 'no_fund_derivable' | 'classifier_low_confidence' | 'orphan'

interface OrphanItem {
  document_id: string
  filename: string
  uploaded_at: string
  parked_at: string
  reason: OrphanReason
  resolved_entity_count: number
  document_profile: Record<string, unknown> | null
}

interface OrphanListResponse {
  orphans: OrphanItem[]
  total: number
  page: number
  page_size: number
}

interface OrphanActionResponse {
  document_id: string
  status: string
  attached_fund_ids: string[]
}

/** Per-row outcome of a manual retry (G2). */
type RowResult = { kind: 'ok' | 'err'; text: string }

const REASON_LABELS: Record<OrphanReason, string> = {
  no_entities_resolved: 'No entities resolved',
  no_fund_derivable: 'No fund derivable',
  classifier_low_confidence: 'Classifier low confidence',
  orphan: 'Parked (legacy)',
}

const REASON_FILTERS: { key: 'all' | OrphanReason; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'no_entities_resolved', label: 'No entities resolved' },
  { key: 'no_fund_derivable', label: 'No fund derivable' },
  { key: 'classifier_low_confidence', label: 'Low confidence' },
]

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]

function ReasonBadge({ reason }: { reason: OrphanReason }) {
  const styles: Record<OrphanReason, { bg: string; text: string }> = {
    no_entities_resolved: { bg: 'rgba(163,93,23,0.12)', text: '#a35d17' },
    no_fund_derivable: { bg: 'rgba(169,132,16,0.15)', text: '#a98410' },
    classifier_low_confidence: { bg: 'rgba(148,163,184,0.18)', text: '#64748b' },
    orphan: { bg: 'rgba(148,163,184,0.15)', text: '#64748b' },
  }
  const s = styles[reason]
  return (
    <span
      className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
      style={{ backgroundColor: s.bg, color: s.text }}
    >
      {REASON_LABELS[reason]}
    </span>
  )
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const seconds = Math.floor((Date.now() - then) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} d ago`
  return formatOfficerDate(iso) ?? iso
}

export default function OrphansPage() {
  const [data, setData] = useState<OrphanListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<'all' | OrphanReason>('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  // G2 — per-row retry: which document is in flight + its last outcome.
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const [rowResults, setRowResults] = useState<Record<string, RowResult>>({})

  const fetchOrphans = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await apiFetch<OrphanListResponse>(
        `/api/orphans?page=${page}&page_size=${pageSize}`
      )
      setData(resp)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orphans')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize])

  useEffect(() => {
    fetchOrphans()
  }, [fetchOrphans])

  // Filter is client-side over the current server page (backend doesn't
  // accept a `reason` query param — see brief). Changing the filter
  // resets to page 1 so the user starts from the top.
  const visible = useMemo(() => {
    if (!data) return []
    if (activeFilter === 'all') return data.orphans
    return data.orphans.filter((o) => o.reason === activeFilter)
  }, [data, activeFilter])

  const changeFilter = useCallback((next: 'all' | OrphanReason) => {
    setActiveFilter(next)
    setPage(1)
  }, [])

  // G2 — officer-initiated retry for ONE parked document. Re-runs fund
  // derivation (action='retry'); on success the doc leaves the queue, on
  // 409 it stays parked with the backend's guidance. Force-attach is not
  // offered here (see file header).
  const retryOrphan = useCallback(
    async (documentId: string) => {
      setRetryingId(documentId)
      setRowResults((r) => {
        const next = { ...r }
        delete next[documentId]
        return next
      })
      try {
        const resp = await apiFetch<OrphanActionResponse>(
          `/api/orphans/${encodeURIComponent(documentId)}/action`,
          { method: 'POST', body: JSON.stringify({ action: 'retry' }) }
        )
        setRowResults((r) => ({
          ...r,
          [documentId]: {
            kind: 'ok',
            text: `Attached to ${resp.attached_fund_ids.length} fund${
              resp.attached_fund_ids.length === 1 ? '' : 's'
            } — pipeline resuming.`,
          },
        }))
        // The row is no longer an orphan; refresh to drop it from the queue.
        fetchOrphans()
      } catch (err) {
        const detail =
          err instanceof ApiError &&
          err.body &&
          typeof err.body === 'object' &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : err instanceof Error
              ? err.message
              : 'retry failed'
        setRowResults((r) => ({ ...r, [documentId]: { kind: 'err', text: detail } }))
      } finally {
        setRetryingId(null)
      }
    },
    [fetchOrphans]
  )

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Parked documents</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {data
              ? `${data.total} total · the system retries automatically when new entities arrive`
              : 'Loading…'}
          </p>
        </div>
        <button
          onClick={fetchOrphans}
          disabled={loading}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {REASON_FILTERS.map((f) => {
          const active = activeFilter === f.key
          return (
            <button
              key={f.key}
              onClick={() => changeFilter(f.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? 'bg-nolos-primary text-white border-nolos-primary'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              {f.label}
            </button>
          )
        })}
      </div>

      {/* Error */}
      {error && (
        <div
          className="bg-white p-6 text-center"
          style={{
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <div className="flex items-center justify-center gap-2 text-sm text-red-600">
            <AlertTriangle size={14} />
            {error}
          </div>
          <button onClick={fetchOrphans} className="mt-3 text-sm text-blue-600 hover:underline">
            Retry
          </button>
        </div>
      )}

      {/* Loading (initial) */}
      {loading && !data && !error && (
        <div
          className="bg-white p-8 text-center text-sm text-slate-500"
          style={{
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          Loading parked documents…
        </div>
      )}

      {/* Empty state */}
      {!loading && data && visible.length === 0 && (
        <div
          className="bg-white p-10 text-center"
          style={{
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <FileText size={28} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">
            {activeFilter === 'all' && data.total === 0
              ? 'No parked documents. The system retries orphans automatically when new entities arrive.'
              : 'No documents on this page match this filter. Clear the filter or check other pages.'}
          </p>
        </div>
      )}

      {/* Table */}
      {data && visible.length > 0 && (
        <div
          className="bg-white overflow-hidden"
          style={{
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3 font-medium">Filename</th>
                <th className="px-4 py-3 font-medium">Reason</th>
                <th className="px-4 py-3 font-medium text-right">Entities</th>
                <th className="px-4 py-3 font-medium">Uploaded</th>
                <th className="px-4 py-3 font-medium">Parked</th>
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => (
                <tr
                  key={o.document_id}
                  className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                  data-testid="orphan-row"
                >
                  <td className="px-4 py-3 text-slate-800">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-slate-400 shrink-0" />
                      <DocumentLink
                        className="truncate max-w-md"
                        document={{ document_id: o.document_id }}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <ReasonBadge reason={o.reason} />
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 font-data">
                    {o.resolved_entity_count}
                  </td>
                  <td
                    className="px-4 py-3 text-slate-500"
                    title={new Date(o.uploaded_at).toLocaleString()}
                  >
                    {formatRelative(o.uploaded_at)}
                  </td>
                  <td
                    className="px-4 py-3 text-slate-500"
                    title={new Date(o.parked_at).toLocaleString()}
                  >
                    {formatRelative(o.parked_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {/* G2 — manual Retry (re-derive on demand) alongside the
                        chain-completion path. Force-attach (pin a fund) is a
                        heavier attribution action, deliberately not exposed
                        here yet — TODO(ale): confirm orphans stay
                        auto-retry-first before adding it. */}
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => retryOrphan(o.document_id)}
                          disabled={retryingId === o.document_id}
                          data-testid="orphan-retry"
                          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-nolos-primary disabled:opacity-50 whitespace-nowrap"
                        >
                          <RefreshCw
                            size={12}
                            className={retryingId === o.document_id ? 'animate-spin' : ''}
                          />
                          {retryingId === o.document_id ? 'Retrying…' : 'Retry'}
                        </button>
                        <Link
                          to={`/v2/manual?document_id=${encodeURIComponent(o.document_id)}`}
                          className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                        >
                          Add the missing record item
                        </Link>
                      </div>
                      {rowResults[o.document_id] && (
                        <span
                          className={`text-[11px] max-w-xs text-right ${
                            rowResults[o.document_id].kind === 'ok'
                              ? 'text-emerald-600'
                              : 'text-amber-700'
                          }`}
                        >
                          {rowResults[o.document_id].text}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <span>Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(1)
              }}
              className="border border-slate-200 rounded px-2 py-1 text-sm bg-white"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span>per page</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1 rounded border border-slate-200 bg-white disabled:opacity-40 hover:border-slate-300"
            >
              Previous
            </button>
            <span>
              Page {data.page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="px-3 py-1 rounded border border-slate-200 bg-white disabled:opacity-40 hover:border-slate-300"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
